// src/operator/shared/host-policy-schema.ts
//
// Schéma canonique des champs de HostPolicy côté UI.
// Miroir typé du contrat C# `agent-windows-csharp/RemoteWS.Host.Service/HostPolicy.cs`.
// Ce fichier est la SOURCE UNIQUE pour l'éditeur de stratégies
// (panels/policy-editor.tsx) et le panel read-only (panels/policy.tsx).
//
// Toute évolution du contrat C# doit être répercutée ici (ajout de champ +
// entrée dans POLICY_FIELDS + éventuel groupe dans POLICY_GROUPS).
//
// Les enums sont alignés sur le C# (cf. HostPolicy.cs). Le types.ts actuel
// contient des valeurs historiques incorrectes (ex. `first_time`) qui sont
// corrigées par ce schéma.

export type PolicyFieldType = "bool" | "enum" | "number" | "string" | "list";

/** Description normalisée d'un champ HostPolicy pour l'éditeur UI. */
export interface PolicyFieldDef {
  /** Clé JSON camelCase = [JsonPropertyName] du C#. */
  key: string;
  /** Label affiché (français). */
  label: string;
  type: PolicyFieldType;
  /** Groupe thématique (cf. POLICY_GROUPS). */
  group: PolicyGroup;
  /** Texte d'aide (tooltip/placeholder). */
  help?: string;
  /** Valeurs autorisées pour un enum. */
  options?: string[];
  /** Bornes pour un number. */
  min?: number;
  max?: number;
  /** Valeur par défaut du contrat C# (pour reset). */
  default: boolean | number | string | string[];
  /** Champ sensible (token) — masqué par défaut dans l'UI. */
  sensitive?: boolean;
}

export type PolicyGroup =
  | "connectivity"
  | "consent"
  | "chat"
  | "privacy"
  | "virtualDisplay"
  | "power"
  | "rds"
  | "rdpShadow"
  | "unattended"
  | "loginScreen"
  | "audit"
  | "enrollment"
  | "trace";

export const POLICY_GROUPS: { id: PolicyGroup; label: string; order: number }[] = [
  { id: "consent", label: "Consentement", order: 1 },
  { id: "privacy", label: "Confidentialité & input", order: 2 },
  { id: "chat", label: "Chat", order: 3 },
  { id: "rds", label: "RDS multi-session", order: 4 },
  { id: "rdpShadow", label: "RDP Shadow", order: 5 },
  { id: "unattended", label: "Accès non assisté", order: 6 },
  { id: "loginScreen", label: "Mire de login", order: 7 },
  { id: "virtualDisplay", label: "Écran virtuel (lab)", order: 8 },
  { id: "power", label: "Maintien éveil / shutdown", order: 9 },
  { id: "audit", label: "Audit", order: 10 },
  { id: "trace", label: "Trace debug", order: 11 },
  { id: "connectivity", label: "Connectivité relai", order: 12 },
  { id: "enrollment", label: "Enrôlement (GPO/MDM)", order: 13 },
];

/**
 * Catalogue exhaustif des champs HostPolicy. Aligné sur HostPolicy.cs.
 * Les clés de provenance (`source`, `readAt`) sont exclues : elles sont
 * calculées côté host, pas éditables.
 */
export const POLICY_FIELDS: PolicyFieldDef[] = [
  // ─── Connectivity ──────────────────────────────────────────────────────
  {
    key: "relayUrl",
    label: "Relay URL imposée",
    type: "string",
    group: "connectivity",
    help: "Si non vide, écrase config.json et CLI --relay.",
    default: "",
  },
  {
    key: "allowRelayOverride",
    label: "Autoriser override relay",
    type: "bool",
    group: "connectivity",
    help: "Si false, RWS_RELAY_URL_OVERRIDE et --relay-override sont ignorés.",
    default: true,
  },

  // ─── Consent ───────────────────────────────────────────────────────────
  {
    key: "viewConsentMode",
    label: "Consentement vue",
    type: "enum",
    group: "consent",
    options: ["never", "always", "firstOperator"],
    help: "Quand demander le consentement avant la diffusion vidéo.",
    default: "never",
  },
  {
    key: "controlConsentMode",
    label: "Consentement contrôle",
    type: "enum",
    group: "consent",
    options: ["never", "always", "onFirstInput"],
    help: "Quand demander le consentement avant input/clipboard write.",
    default: "never",
  },
  {
    key: "consentTimeoutSeconds",
    label: "Timeout consentement (s)",
    type: "number",
    group: "consent",
    min: 5,
    max: 600,
    default: 60,
  },
  {
    key: "consentTimeoutAction",
    label: "Action au timeout",
    type: "enum",
    group: "consent",
    options: ["deny", "approve"],
    help: "Refuser (deny) ou approuver (approve) si l'utilisateur ne répond pas.",
    default: "deny",
  },
  {
    key: "showOperatorIdentity",
    label: "Afficher l'identité opérateur",
    type: "bool",
    group: "consent",
    help: "Affiche nom/prénom de l'opérateur dans le popup utilisateur.",
    default: true,
  },
  {
    key: "allowViewOnlyWithoutControl",
    label: "Vue sans contrôle autorisée",
    type: "bool",
    group: "consent",
    help: "Permet le screen sharing en lecture seule.",
    default: true,
  },

  // ─── Chat ──────────────────────────────────────────────────────────────
  {
    key: "allowUserOperatorChat",
    label: "Chat autorisé",
    type: "bool",
    group: "chat",
    default: true,
  },
  {
    key: "chatAvailableBeforeConsent",
    label: "Chat avant consentement",
    type: "bool",
    group: "chat",
    help: "Permet de discuter avant acceptation de la vue/contrôle.",
    default: true,
  },
  {
    key: "chatTranscriptAuditMode",
    label: "Mode d'audit du chat",
    type: "enum",
    group: "chat",
    options: ["metadata", "full", "disabled"],
    default: "metadata",
  },

  // ─── Trace ─────────────────────────────────────────────────────────────
  {
    key: "traceAllowed",
    label: "Trace debug autorisée",
    type: "bool",
    group: "trace",
    help: "Capture de debug côté agent (déclenchée superadmin).",
    default: true,
  },
  {
    key: "traceBodiesAllowed",
    label: "Trace des bodies",
    type: "bool",
    group: "trace",
    help: "Capture des BODIES (rédigés) dans la trace agent. Sensible.",
    default: false,
  },

  // ─── Privacy & input ───────────────────────────────────────────────────
  {
    key: "allowPrivacyScreen",
    label: "Écran noir autorisé",
    type: "bool",
    group: "privacy",
    default: true,
  },
  {
    key: "privacyScreenRequiresConsent",
    label: "Consentement requis (privacy)",
    type: "bool",
    group: "privacy",
    help: "Évite qu'un opérateur masque l'écran sans acceptation utilisateur.",
    default: true,
  },
  {
    key: "allowLocalInputBlock",
    label: "Blocage input local autorisé",
    type: "bool",
    group: "privacy",
    help: "BlockInput() — gel clavier/souris local.",
    default: true,
  },
  {
    key: "localInputBlockRequiresPrivacyScreen",
    label: "Blocage input nécessite privacy",
    type: "bool",
    group: "privacy",
    help: "BlockInput utilisable seulement avec écran noir actif.",
    default: true,
  },

  // ─── Virtual display (lab) ─────────────────────────────────────────────
  {
    key: "allowVirtualOperatorDisplay",
    label: "Écran virtuel caché autorisé",
    type: "bool",
    group: "virtualDisplay",
    help: "Option écran virtuel (non testé en prod).",
    default: false,
  },
  {
    key: "virtualDisplayProvider",
    label: "Provider d'écran virtuel",
    type: "enum",
    group: "virtualDisplay",
    options: ["none", "virtualdrivers-vdd", "vdd", "remotews-idd"],
    default: "none",
  },
  {
    key: "virtualDisplayRequiresPrivacyScreen",
    label: "Nécessite privacy screen",
    type: "bool",
    group: "virtualDisplay",
    default: true,
  },
  {
    key: "allowWindowMoveToVirtualDisplay",
    label: "Déplacer fenêtres lancées",
    type: "bool",
    group: "virtualDisplay",
    default: false,
  },
  {
    key: "allowMoveExistingWindowsToVirtualDisplay",
    label: "Déplacer fenêtres existantes",
    type: "bool",
    group: "virtualDisplay",
    default: false,
  },
  {
    key: "restoreMovedWindowsOnStop",
    label: "Restaurer fenêtres à l'arrêt",
    type: "bool",
    group: "virtualDisplay",
    default: true,
  },

  // ─── Power & shutdown ──────────────────────────────────────────────────
  {
    key: "allowPowerHoldDuringRemoteWork",
    label: "Maintien éveil autorisé",
    type: "bool",
    group: "power",
    help: "SetThreadExecutionState pendant une opération longue.",
    default: true,
  },
  {
    key: "powerHoldMaxMinutes",
    label: "TTL maintien éveil (min)",
    type: "number",
    group: "power",
    min: 1,
    max: 1440,
    default: 120,
  },
  {
    key: "allowShutdownBlockDuringRemoteWork",
    label: "Blocage shutdown autorisé",
    type: "bool",
    group: "power",
    help: "ShutdownBlockReasonCreate pendant une opération.",
    default: true,
  },
  {
    key: "shutdownBlockMaxMinutes",
    label: "TTL blocage shutdown (min)",
    type: "number",
    group: "power",
    min: 1,
    max: 1440,
    default: 60,
  },
  {
    key: "shutdownBlockReason",
    label: "Raison shutdown",
    type: "string",
    group: "power",
    help: "Raison courte affichée par Windows pendant le blocage.",
    default: "Maintenance RemoteWS en cours",
  },

  // ─── RDS ───────────────────────────────────────────────────────────────
  {
    key: "rdsTargetMode",
    label: "Cible RDS",
    type: "enum",
    group: "rds",
    options: ["explicit", "consoleOnly", "activeSingle"],
    help: "Règle de sélection de session RDS cible.",
    default: "activeSingle",
  },
  {
    key: "rdpAdaptiveResolutionAllowed",
    label: "Résolution RDP adaptative",
    type: "bool",
    group: "rds",
    help: "Pilote la résolution virtuelle d'une session RDP au viewport opérateur.",
    default: false,
  },

  // ─── RDP Shadow ────────────────────────────────────────────────────────
  {
    key: "rdpShadowMode",
    label: "Mode shadow",
    type: "enum",
    group: "rdpShadow",
    options: ["disabled", "helper_inject", "credential_prompt"],
    default: "disabled",
  },
  {
    key: "rdpShadowDefaultAction",
    label: "Action shadow par défaut",
    type: "enum",
    group: "rdpShadow",
    options: ["view", "control"],
    default: "view",
  },
  {
    key: "rdpShadowSuppressNativePrompt",
    label: "Supprimer popup natif mstsc",
    type: "bool",
    group: "rdpShadow",
    help: "Ajoute /noconsentprompt ; requiert la GPO Microsoft correspondante.",
    default: true,
  },
  {
    key: "rdpShadowMaxMinutes",
    label: "TTL shadow (min)",
    type: "number",
    group: "rdpShadow",
    min: 1,
    max: 1440,
    default: 120,
  },
  {
    key: "rdpShadowAllowedGroups",
    label: "Groupes autorisés (shadow)",
    type: "string",
    group: "rdpShadow",
    help: "Liste séparée par virgules (ex: Administrators).",
    default: "Administrators",
  },
  {
    key: "rdpShadowConsent",
    label: "Consentement shadow",
    type: "enum",
    group: "rdpShadow",
    options: ["direct", "credential_prompt"],
    default: "credential_prompt",
  },

  // ─── Unattended access ─────────────────────────────────────────────────
  {
    key: "unattendedAccessAllowed",
    label: "Accès non assisté autorisé",
    type: "bool",
    group: "unattended",
    help: "Prise en main quand aucun user n'est connecté (LogonUser éphémère).",
    default: false,
  },
  {
    key: "unattendedAccessAllowedGroups",
    label: "Groupes autorisés (unattended)",
    type: "string",
    group: "unattended",
    default: "Administrators",
  },
  {
    key: "unattendedAccessAutoLogoffMinutes",
    label: "Auto-logoff (min)",
    type: "number",
    group: "unattended",
    min: 1,
    max: 1440,
    default: 120,
  },

  // ─── Login screen ──────────────────────────────────────────────────────
  {
    key: "systemViewLoginScreenAllowed",
    label: "Voir la mire de login autorisé",
    type: "bool",
    group: "loginScreen",
    help: "Capture-only dans winsta0\\Winlogon via Host.Service SYSTEM.",
    default: false,
  },
  {
    key: "systemViewLoginScreenAutoStart",
    label: "Démarrage auto vue mire",
    type: "bool",
    group: "loginScreen",
    default: true,
  },

  // ─── Audit ─────────────────────────────────────────────────────────────
  {
    key: "auditPolicyDecisions",
    label: "Audit des décisions",
    type: "bool",
    group: "audit",
    help: "Journalise chaque décision de policy.",
    default: true,
  },

  // ─── Enrollment (GPO/MDM) ──────────────────────────────────────────────
  {
    key: "installToken",
    label: "Install token (GPO)",
    type: "string",
    group: "enrollment",
    help: "Token pré-provisionné pour enrôlement silencieux au boot. Sensible.",
    default: "",
    sensitive: true,
  },
  {
    key: "installTokenAutoConsume",
    label: "Consommation auto du token",
    type: "bool",
    group: "enrollment",
    help: "Consomme l'InstallToken au 1er boot et le supprime (one-shot).",
    default: true,
  },
];

/** Index key → field def pour accès rapide. */
export const POLICY_FIELDS_BY_KEY: Record<string, PolicyFieldDef> = Object.fromEntries(
  POLICY_FIELDS.map((f) => [f.key, f]),
);

/** Type policy (Record<string, valeur JSON>) — volontairement permissif. */
export type PolicyObject = Record<string, boolean | number | string | string[] | null | undefined>;

/** Ensemble des clés connues du schéma (utile pour distinguer legacy/inconnu). */
export function listKnownKeys(): Set<string> {
  return new Set(POLICY_FIELDS.map((f) => f.key));
}

/** Champs présents dans la policy mais absents du schéma (legacy/inconnus). */
export function listUnknownKeys(policy: PolicyObject): string[] {
  const known = listKnownKeys();
  const provenance = new Set(["source", "readAt", "precedence", "trace"]);
  return Object.keys(policy).filter((k) => !known.has(k) && !provenance.has(k));
}

/**
 * Applique un preset sur une policy courante.
 * @param mode "merge" (défaut) — surcharge champ par champ, conserve le reste.
 *              "replace" — la policy devient exactement celle du preset.
 */
export function applyPreset(
  current: PolicyObject,
  preset: PolicyObject,
  mode: "merge" | "replace" = "merge",
): PolicyObject {
  if (mode === "replace") return { ...preset };
  return { ...current, ...preset };
}

/** Diff champ par champ entre deux policies. */
export interface PolicyDiffEntry {
  key: string;
  before: unknown;
  after: unknown;
  /** "added" | "removed" | "changed" */
  status: "added" | "removed" | "changed";
}

export function diffPolicies(before: PolicyObject, after: PolicyObject): PolicyDiffEntry[] {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const out: PolicyDiffEntry[] = [];
  for (const key of keys) {
    const inBefore = Object.prototype.hasOwnProperty.call(before, key);
    const inAfter = Object.prototype.hasOwnProperty.call(after, key);
    if (inBefore && !inAfter) {
      out.push({ key, before: before[key], after: undefined, status: "removed" });
    } else if (!inBefore && inAfter) {
      out.push({ key, before: undefined, after: after[key], status: "added" });
    } else if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
      out.push({ key, before: before[key], after: after[key], status: "changed" });
    }
  }
  return out;
}

/** true si deux policies sont identiques (aucun diff). */
export function policiesEqual(a: PolicyObject, b: PolicyObject): boolean {
  return diffPolicies(a, b).length === 0;
}
